/**
 * Resizes an image in the browser before it is uploaded.
 *
 * A photo straight off a modern phone is 4000 pixels wide and several megabytes. Nothing on this
 * site displays more than about 1600, so uploading the original wastes the seller's mobile data on
 * the slowest step of posting an ad, and then costs storage to keep pixels no one will see.
 *
 * Doing it here also means the server never decodes untrusted image data, which is historically
 * where image handling goes wrong, and it removes the need for a native imaging library on a Linux
 * App Service entirely.
 */

const MAX_FULL_EDGE = 1600;
const MAX_THUMB_EDGE = 480;

export interface ResizedImage {
  full: Blob;
  thumbnail: Blob;
  width: number;
  height: number;
  contentType: string;
}

function scaledSize(width: number, height: number, maxEdge: number) {
  const largest = Math.max(width, height);

  // Never upscale. Enlarging a small photo adds bytes and removes nothing but sharpness.
  if (largest <= maxEdge) {
    return { width, height };
  }

  const ratio = maxEdge / largest;

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

async function draw(bitmap: ImageBitmap, maxEdge: number, type: string, quality: number): Promise<Blob> {
  const { width, height } = scaledSize(bitmap.width, bitmap.height, maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas nije dostupan.');
  }

  // Browsers downscale in one step by default, which produces visible aliasing on a car's grille
  // and wheels. High-quality smoothing is the difference between a photo that looks sharp and one
  // that looks like a screenshot.
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, type, quality),
  );

  if (!blob) {
    throw new Error('Obrada fotografije nije uspela.');
  }

  return blob;
}

export async function resizeForUpload(file: File): Promise<ResizedImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Fajl nije slika.');
  }

  // createImageBitmap applies EXIF orientation, so photos taken sideways on a phone arrive the
  // right way up instead of being rotated 90 degrees for everyone who views the ad.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

  try {
    // WebP is roughly a third smaller than JPEG at the same quality and is supported by every
    // browser that matters now, but Safari was late enough that a fallback is still worth keeping.
    const supportsWebp = document
      .createElement('canvas')
      .toDataURL('image/webp')
      .startsWith('data:image/webp');

    const contentType = supportsWebp ? 'image/webp' : 'image/jpeg';

    const [full, thumbnail] = await Promise.all([
      draw(bitmap, MAX_FULL_EDGE, contentType, 0.82),
      // The thumbnail is compressed harder: it is displayed at a fraction of the size, where the
      // artefacts that would be visible in the gallery are simply not resolvable.
      draw(bitmap, MAX_THUMB_EDGE, contentType, 0.72),
    ]);

    const { width, height } = scaledSize(bitmap.width, bitmap.height, MAX_FULL_EDGE);

    return { full, thumbnail, width, height, contentType };
  } finally {
    // Bitmaps hold decoded pixel data, which for a 12-megapixel photo is around 48 MB. Uploading a
    // dozen without releasing them is enough to be killed by the browser on a mid-range phone.
    bitmap.close();
  }
}
